import { Component } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

/**
 * Frontera de error de React.
 *
 * Sin esto, cualquier excepción en el árbol de componentes desmonta la
 * aplicación completa y el operador se queda ante una pantalla en
 * blanco, sin saber si su último movimiento de inventario se registró.
 *
 * Aquí se captura el fallo, se muestra una salida clara y se ofrece
 * recargar. El detalle técnico solo se despliega bajo demanda: al
 * operador de almacén no le sirve un stack trace.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    this.setState({ info });
    console.error('[TEGO] Error no controlado en la interfaz:', error, info);
    this.reportar(error, info);
  }

  // Envia el fallo al servidor para que llegue solo (en casa de un cliente
  // no habra captura de pantalla que pedir). El usuario y la organizacion
  // los deriva el backend del token; aqui solo se adjunta. Reportar nunca
  // debe, a su vez, tumbar la aplicacion.
  async reportar(error, info) {
    try {
      // LA CREDENCIAL YA NO SE ADJUNTA AQUÍ · hallazgo A-4. Vivía en
      // `localStorage` y ahora es una cookie `HttpOnly` que este código
      // no puede leer — ni falta: `/api/errores-cliente` está en
      // nuestro mismo origen, así que el navegador la manda sola y el
      // backend sigue derivando de ella el usuario y la empresa.
      const headers = { 'Content-Type': 'application/json' };
      await fetch('/api/errores-cliente', {
        method: 'POST',
        headers,
        keepalive: true,
        body: JSON.stringify({
          mensaje: String(error?.message || error).slice(0, 2000),
          stack: error?.stack ? String(error.stack).slice(0, 20000) : null,
          component_stack: info?.componentStack
            ? String(info.componentStack).slice(0, 20000)
            : null,
          ruta: window.location.pathname + window.location.hash,
          version: 'tego-web',
        }),
      });
    } catch {
      /* silencioso: la telemetria no puede provocar otro fallo */
    }
  }

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6 font-sans">
        <div className="max-w-lg w-full bg-white rounded-2xl border border-gray-200 shadow-sm p-8">
          <div className="w-14 h-14 bg-red-50 rounded-xl flex items-center justify-center mb-5">
            <AlertTriangle className="w-7 h-7 text-red-500" />
          </div>

          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Algo falló en la aplicación
          </h1>
          <p className="text-gray-600 mb-2 leading-relaxed">
            La pantalla no pudo cargarse correctamente. Ningún dato se ha
            perdido: las operaciones ya confirmadas quedaron guardadas en el
            servidor.
          </p>
          <p className="text-gray-600 mb-6 leading-relaxed">
            Si el problema se repite, avisa al administrador del sistema e
            indícale qué estabas haciendo.
          </p>

          <div className="flex gap-3">
            <button
              onClick={() => window.location.reload()}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-xl flex items-center gap-2 transition-colors"
            >
              <RefreshCw className="w-4 h-4" /> Recargar
            </button>
            <button
              onClick={() => this.setState({ error: null, info: null })}
              className="bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 font-semibold py-3 px-6 rounded-xl transition-colors"
            >
              Intentar continuar
            </button>
          </div>

          <details className="mt-6 group">
            <summary className="text-xs font-semibold text-gray-400 uppercase tracking-wider cursor-pointer hover:text-gray-600">
              Detalle técnico
            </summary>
            <pre className="mt-3 text-[11px] bg-slate-50 border border-gray-200 rounded-lg p-3 overflow-x-auto text-slate-700 whitespace-pre-wrap">
              {String(this.state.error?.stack || this.state.error)}
              {this.state.info?.componentStack
                ? `\n\n— Componente —${this.state.info.componentStack}`
                : ''}
            </pre>
          </details>
        </div>
      </div>
    );
  }
}
